import {AfterViewInit, DOCUMENT, Directive, ElementRef, OnDestroy, effect, inject, input, untracked} from '@angular/core';
import {Rgba, parseColor, sameColor, toRgbString} from '@app/_components/color-panel/color-format';
import {COLOR_PANEL_PALETTE_RGB} from '@app/_components/color-panel/color-panel.component';
import {ColorPanelRef, ColorPanelResult, ColorPanelService} from '@app/_components/color-panel/color-panel.service';
import {ColorPanelState, EMPTY_COLOR_STATE, addRecentColor, setStateColor, stateColor, storedColor} from '@app/_components/color-panel/color-panel-state';

/**
 * Turns a text input into a colour field: a swatch beside it, and the colour panel under it on a click. What it
 * stores is what Spectrum stored, byte for byte:
 * - text typed with the panel closed, or then left with Esc or Cancel, is kept as typed;
 * - a Choose, Enter or click outside stores the formatted colour, and only when it differs from the colour at
 *   opening (an empty colour never equals one, so it always stores `''`);
 * - the format is `#rrggbb`, `#rrggbbaa` with `showAlpha` and transparency, or `rgb()`/`rgba()` with
 *   `appColorPickerAlpha`; clearing stores `''`.
 * The value lands in the input with an `input` and a `change` event, so ngModel, form controls and (change)
 * bindings all see it.
 *
 * From the keyboard, ArrowDown in the field opens the panel with focus in it, and Tab or ArrowDown moves into a panel
 * a click opened; Esc, Cancel and Choose bring focus back to the field.
 */
@Directive({selector: '[appColorPicker]'})
export class ColorPickerDirective implements AfterViewInit, OnDestroy {
  private el = inject<ElementRef<HTMLInputElement>>(ElementRef);
  private document = inject(DOCUMENT);
  private colorPanel = inject(ColorPanelService);

  readonly value = input(undefined);
  /** Stores rgb strings, with an alpha bar. */
  readonly appColorPickerAlpha = input(false);
  /** Shows only the swatch, and stores every move in the panel; Cancel puts the text at opening back. */
  readonly appColorPickerSwatchOnly = input(false);
  /** Marks the field when it has no colour. */
  readonly appColorPickerEmptyIndicator = input(false);
  /** Adds the alpha bar to a hex picker; a see-through colour is stored as `#rrggbbaa`. */
  readonly showAlpha = input(false);
  readonly allowEmpty = input(true);

  private container: HTMLElement | null = null;
  private swatchFill: HTMLElement | null = null;
  private state: ColorPanelState = EMPTY_COLOR_STATE;
  private recent: string[] = [];
  private panel: ColorPanelRef | null = null;
  /** The value binding last applied, so only a change to it moves the colour. */
  private appliedValue: unknown;
  /** The input's text the colour was last read from or written as. */
  private syncedText = '';
  /** The user has typed since then. */
  private typed = false;
  /** The input's text at opening, and whether a swatch-only move has replaced it. */
  private textAtOpen = '';
  private movedText = false;
  private writing = false;
  private destroyed = false;
  private readonly cleanups: (() => void)[] = [];

  constructor() {
    effect(() => {
      const value = this.value();
      untracked(() => {
        if (this.container && value !== this.appliedValue) {
          this.appliedValue = value;
          this.syncValue(value);
        }
      });
    });
    effect(() => {
      const swatchOnly = this.appColorPickerSwatchOnly();
      const emptyIndicator = this.appColorPickerEmptyIndicator();
      untracked(() => {
        this.container?.classList.toggle('app-color-picker-swatch-only', swatchOnly);
        this.container?.classList.toggle('app-color-picker-empty-indicator', emptyIndicator);
        this.updateEmptyState(this.el.nativeElement.value);
      });
    });
  }

  ngAfterViewInit(): void {
    this.wrap();
    this.appliedValue = this.value();
    this.syncValue(this.value() || this.el.nativeElement.value);

    const field = this.el.nativeElement;
    this.listen(field, 'click', (event) => {
      event.stopPropagation();
      this.open();
    });
    this.listen(this.container.querySelector('.app-color-picker-swatch'), 'click', (event) => {
      event.stopPropagation();
      event.preventDefault();
      this.open();
    });
    for (const label of Array.from(field.labels || [])) {
      this.listen(label, 'click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.open();
      });
    }
    this.listen(field, 'input', () => {
      if (!this.writing) {
        this.typed = true;
      }
    });
    this.listen(field, 'change', () => {
      if (!this.writing) {
        this.readText(field.value);
      }
    });
    this.listen(field, 'keydown', (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        this.readText(field.value);
        this.panel?.close('choose');
      } else if (event.key === 'ArrowDown' || (event.key === 'Tab' && !event.shiftKey && this.panel?.isOpen)) {
        // The panel sits at the end of the page, so Tab would skip it.
        event.preventDefault();
        if (this.panel?.isOpen) {
          this.panel.focus();
        } else {
          this.open(true);
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.panel?.close('dismiss');
    this.cleanups.forEach((cleanup) => cleanup());
    this.unwrap();
  }

  private open(focus = false): void {
    const field = this.el.nativeElement;
    // :disabled also covers a disabled fieldset around the field, as sb-form's while it loads or saves.
    if (field.matches(':disabled') || this.panel?.isOpen) {
      return;
    }
    // A form control set from code changes the text without telling the picker.
    if (!this.typed && field.value !== this.syncedText) {
      this.readText(field.value);
    }
    this.textAtOpen = field.value;
    this.movedText = false;
    field.setAttribute('aria-expanded', 'true');
    this.panel = this.colorPanel.open(field, {
      state: this.state,
      alpha: this.appColorPickerAlpha() || this.showAlpha(),
      allowEmpty: this.allowEmpty(),
      format: this.appColorPickerAlpha() ? 'rgb' : 'hex',
      recent: this.recent,
      closeOnPalette: true,
      focus,
      moved: (color) => this.onMoved(color),
      closed: (result) => this.onClosed(result),
    });
  }

  private onMoved(color: Rgba | null): void {
    this.paint(color);
    if (this.appColorPickerSwatchOnly()) {
      this.write(this.format(color));
      this.movedText = true;
    }
  }

  private onClosed({reason, state, last, initial}: ColorPanelResult): void {
    this.panel = null;
    if (this.destroyed) {
      return;
    }
    this.el.nativeElement.setAttribute('aria-expanded', 'false');
    // Spectrum's Esc only hid the panel, so what a swatch-only picker stored while it was open stays stored.
    this.state = this.movedText && reason === 'dismiss' ? last : state;
    const color = this.color();
    const initialColor = stateColor(initial, this.allowEmpty());
    if (reason === 'choose') {
      this.addRecent(color);
      const text = this.format(color);
      // A swatch-only picker stored this colour with its last move.
      if (!sameColor(color, initialColor) && !(this.movedText && text === this.syncedText)) {
        this.write(text);
      }
    } else if (reason === 'cancel') {
      if (this.movedText) {
        this.write(this.textAtOpen);
      } else if (!initialColor) {
        this.write('');
      }
    }
    this.paint(color);
  }

  /** Spectrum's reading of a new value: an empty one empties the field, any other moves the colour. */
  private syncValue(value: unknown): void {
    const field = this.el.nativeElement;
    if (value === null || value === undefined || String(value).trim() === '') {
      if (this.allowEmpty()) {
        this.state = setStateColor(this.state, null, true);
      }
      if (field.value !== '') {
        field.value = '';
      }
    } else {
      this.state = setStateColor(this.state, String(value), this.allowEmpty());
      // A binding that only echoes what is being typed would fill the recent row with half-typed colours.
      if (field.value !== String(value) || !this.typed) {
        this.addRecent(this.color());
      }
      if (field.value !== String(value)) {
        field.value = String(value);
      }
    }
    this.syncedText = field.value;
    this.typed = false;
    this.updateEmptyState(field.value);
    this.paint(this.color());
  }

  /** Typed text, once the input commits it: empty clears, unreadable text changes nothing, the text stays. */
  private readText(text: string): void {
    this.syncedText = text;
    this.typed = false;
    if (text !== '' || !this.allowEmpty()) {
      const color = parseColor(text);
      if (!color.ok) {
        return;
      }
      this.state = setStateColor(this.state, color, this.allowEmpty());
    } else {
      this.state = setStateColor(this.state, null, true);
    }
    this.addRecent(this.color());
    this.paint(this.color());
    this.panel?.panel.state.set(this.state);
  }

  private write(value: string): void {
    const field = this.el.nativeElement;
    field.value = value;
    this.syncedText = value;
    this.typed = false;
    this.updateEmptyState(value);
    this.writing = true;
    try {
      field.dispatchEvent(new Event('input', {bubbles: true}));
      field.dispatchEvent(new Event('change', {bubbles: true}));
    } finally {
      this.writing = false;
    }
  }

  private format(color: Rgba | null): string {
    return storedColor(color, {alpha: this.appColorPickerAlpha(), showAlpha: this.showAlpha()});
  }

  private color(): Rgba | null {
    return stateColor(this.state, this.allowEmpty());
  }

  private addRecent(color: Rgba | null): void {
    this.recent = addRecentColor(this.recent, color, COLOR_PANEL_PALETTE_RGB);
  }

  private paint(color: Rgba | null): void {
    if (this.swatchFill) {
      this.swatchFill.style.backgroundColor = color ? toRgbString(color) : '';
    }
  }

  private updateEmptyState(value: string): void {
    this.container?.classList.toggle('app-color-picker-empty', this.appColorPickerEmptyIndicator() && !String(value ?? '').trim());
  }

  /**
   * Spectrum's markup: the input and a swatch as wide as the wrapped input is tall, in a span that keeps the input's
   * margin. The swatch is measured before the span gets `app-color-picker-input-container`, which pages style, as
   * Spectrum measured it before this directive added that class.
   */
  private wrap(): void {
    const field = this.el.nativeElement;
    const container = this.document.createElement('span');
    container.className = 'app-color-picker-wrapper';
    container.style.margin = getComputedStyle(field).margin;
    if (getComputedStyle(field).display === 'block') {
      container.style.display = 'flex';
    }
    field.parentNode.insertBefore(container, field);
    container.appendChild(field);
    const style = getComputedStyle(field);
    const swatch = this.document.createElement('div');
    swatch.className = 'app-color-picker-swatch';
    swatch.style.width = `${field.getBoundingClientRect().height}px`;
    swatch.style.borderRadius = style.borderRadius;
    swatch.style.border = style.border;
    const fill = this.document.createElement('div');
    fill.className = 'app-color-picker-swatch-fill';
    swatch.appendChild(fill);
    container.insertBefore(swatch, field);
    field.classList.add('app-color-picker-input');
    field.setAttribute('aria-haspopup', 'dialog');
    field.setAttribute('aria-expanded', 'false');
    container.classList.add('app-color-picker-input-container');
    container.classList.toggle('app-color-picker-swatch-only', this.appColorPickerSwatchOnly());
    container.classList.toggle('app-color-picker-empty-indicator', this.appColorPickerEmptyIndicator());
    this.container = container;
    this.swatchFill = fill;
  }

  /** Puts the input back where it was; when Angular has already taken the input out, the wrapper goes with it. */
  private unwrap(): void {
    const field = this.el.nativeElement;
    if (this.container && field.parentNode === this.container && this.container.parentNode) {
      this.container.parentNode.insertBefore(field, this.container);
    }
    this.container?.remove();
    field.classList.remove('app-color-picker-input');
    field.removeAttribute('aria-haspopup');
    field.removeAttribute('aria-expanded');
    this.container = null;
    this.swatchFill = null;
  }

  private listen(target: EventTarget | null, type: string, handler: (event: Event) => void): void {
    if (!target) {
      return;
    }
    target.addEventListener(type, handler);
    this.cleanups.push(() => target.removeEventListener(type, handler));
  }
}
